const formatDuration = (ms) => {
  if (!Number.isFinite(ms) || ms < 0) {
    return null;
  }
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(ms < 10000 ? 1 : 0)}s`;
};

const FALLBACK_TOOLTIP = 'Parallel processing is turned on for this loop, but a step inside it cannot run in parallel (for example a Break, a memory write, or a step that reads the previous item). Open the loop’s Parallel Processing settings to see which step is blocking it.';
const NESTED_TOOLTIP = 'This loop sits inside another loop that already runs its items in parallel. Only the outermost parallel loop runs items concurrently.';

/**
 * Summary-tab notice for an Iterate step's run-log response: how the loop ran when parallel
 * processing was on. Reads `meta.parallel` (written by runParallelIterations) or the fallback
 * warnings the runtime puts on the response when it had to run one item at a time.
 */
const getParallelLoopNotice = (response) => {
  const parallel = response?.meta?.parallel;
  if (parallel && Number.isInteger(parallel.completedCount)) {
    const items = `${parallel.completedCount}${Number.isInteger(parallel.itemCount) && parallel.itemCount !== parallel.completedCount ? ` of ${parallel.itemCount}` : ''} item${parallel.completedCount === 1 ? '' : 's'}`;
    const peak = Number.isInteger(parallel.maxWindowReached) ? parallel.maxWindowReached : null;
    const duration = formatDuration(parallel.durationMs);
    const text = `Ran in parallel: ${items}${peak ? `, up to ${peak} at a time` : ''}${duration ? ` in ${duration}` : ''}.`;
    const tooltipParts = [];
    if (parallel.adaptive === true) {
      tooltipParts.push(`Adaptive window, max ${parallel.maxWindow ?? peak}.`);
    } else if (Number.isInteger(parallel.maxWindow)) {
      tooltipParts.push(`Fixed window of ${parallel.maxWindow}.`);
    }
    if (Number.isInteger(parallel.windowReductions) && parallel.windowReductions > 0) {
      tooltipParts.push(`Slowed down ${parallel.windowReductions} time${parallel.windowReductions === 1 ? '' : 's'} after the API pushed back.`);
    }
    if (parallel.outcome && parallel.outcome !== 'success') {
      tooltipParts.push(`Loop ended early: ${parallel.outcome}. Items already running finished; no new ones started.`);
    }
    return { type: 'info', text, tooltip: tooltipParts.join(' ') || undefined };
  }

  const warnings = Array.isArray(response?.meta?.warnings) ? response.meta.warnings : [];
  if (warnings.some((warning) => warning?.code === 'PARALLEL_LOOP_RAN_SEQUENTIALLY')) {
    return { type: 'warning', text: 'Parallel processing is on, but this loop ran one item at a time.', tooltip: FALLBACK_TOOLTIP };
  }
  if (warnings.some((warning) => warning?.code === 'PARALLEL_LOOP_NESTED')) {
    return { type: 'warning', text: 'This nested loop ran one item at a time.', tooltip: NESTED_TOOLTIP };
  }

  return null;
};

export default getParallelLoopNotice;
